import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Language = "tr" | "en";
export type LocalizedCopy = Record<Language, string>;
type Replacements = Record<string, string | number>;

type LanguageValue = {
  language: Language;
  setLanguage(language: Language): void;
  t(copy: LocalizedCopy, replacements?: Replacements): string;
  error(value: unknown, fallback?: LocalizedCopy): string;
  status(value: string): string;
  kind(value: string): string;
  mode(value: string): string;
};

const STORAGE_KEY = "dora:language:v1";
const LanguageContext = createContext<LanguageValue | null>(null);

const statusCopy: Record<string, LocalizedCopy> = {
  queued: { tr: "Sırada", en: "Queued" },
  pending: { tr: "Bekliyor", en: "Pending" },
  processing: { tr: "İşleniyor", en: "Processing" },
  completed: { tr: "Tamamlandı", en: "Completed" },
  failed: { tr: "Başarısız", en: "Failed" },
  timed_out: { tr: "Zaman aşımına uğradı", en: "Timed out" },
};

const kindCopy: Record<string, LocalizedCopy> = {
  image: { tr: "Görsel", en: "Image" },
  video: { tr: "Video", en: "Video" },
};

const modeCopy: Record<string, LocalizedCopy> = {
  "text-to-image": { tr: "Metinden görsele", en: "Text to image" },
  "image-to-image": { tr: "Görselden görsele", en: "Image to image" },
  "text-to-video": { tr: "Metinden videoya", en: "Text to video" },
  "image-to-video": { tr: "Görselden videoya", en: "Image to video" },
};

const errorCopy: Record<string, LocalizedCopy> = {
  REQUEST_FAILED: { tr: "İstek tamamlanamadı.", en: "Request failed." },
  INTERNAL_ERROR: {
    tr: "Beklenmeyen bir sunucu hatası oluştu.",
    en: "An unexpected server error occurred.",
  },
  AUTH_REQUIRED: {
    tr: "Devam etmek için giriş yapın.",
    en: "Sign in to continue.",
  },
  ADMIN_REQUIRED: {
    tr: "Bu işlem için yönetici yetkisi gerekiyor.",
    en: "Administrator access is required.",
  },
  INVALID_SIGNUP: {
    tr: "Kayıt bilgilerinizi kontrol edin.",
    en: "Check your registration details.",
  },
  ACCOUNT_CONFLICT: {
    tr: "Bu e-posta veya kullanıcı adı zaten kullanılıyor.",
    en: "This email or username is already in use.",
  },
  INVALID_CREDENTIALS: {
    tr: "E-posta, kullanıcı adı veya şifre hatalı.",
    en: "Email, username, or password is incorrect.",
  },
  OAUTH_DISABLED: {
    tr: "Google ile giriş şu anda kullanılamıyor.",
    en: "Google sign-in is currently unavailable.",
  },
  OAUTH_STATE_MISMATCH: {
    tr: "Giriş oturumu doğrulanamadı. Lütfen yeniden deneyin.",
    en: "The sign-in session could not be verified. Please try again.",
  },
  UNSUPPORTED_MEDIA: {
    tr: "Bu medya türü desteklenmiyor.",
    en: "This media type is not supported.",
  },
  INVALID_UPLOAD: {
    tr: "Yüklenen dosya geçersiz veya çok büyük.",
    en: "The uploaded file is invalid or too large.",
  },
  MEDIA_TYPE_MISMATCH: {
    tr: "Dosya uzantısı ile medya türü eşleşmiyor.",
    en: "The file extension does not match the media type.",
  },
  PROFILE_NOT_FOUND: { tr: "Profil bulunamadı.", en: "Profile was not found." },
  INVALID_USERNAME: {
    tr: "Geçerli bir kullanıcı adı girin.",
    en: "Enter a valid username.",
  },
  USERNAME_TAKEN: {
    tr: "Bu kullanıcı adı zaten alınmış.",
    en: "This username is already taken.",
  },
  GENERATION_NOT_FOUND: {
    tr: "Üretim bulunamadı veya artık kullanılamıyor.",
    en: "The generation was not found or is no longer available.",
  },
  MODEL_UNAVAILABLE: {
    tr: "Seçilen model şu anda kullanılamıyor.",
    en: "The selected model is currently unavailable.",
  },
  INSUFFICIENT_CREDITS: {
    tr: "Bu üretim için yeterli krediniz yok.",
    en: "You do not have enough credits for this generation.",
  },
  USER_NOT_ACTIVE: {
    tr: "Hesabınız şu anda etkin değil.",
    en: "Your account is not currently active.",
  },
  SELF_FOLLOW_DENIED: {
    tr: "Kendinizi takip edemezsiniz.",
    en: "You cannot follow yourself.",
  },
  INVALID_COMMENT: {
    tr: "Yorum boş olamaz ve 500 karakteri aşamaz.",
    en: "A comment cannot be empty or exceed 500 characters.",
  },
  COMMENT_NOT_FOUND: { tr: "Yorum bulunamadı.", en: "Comment was not found." },
  INVALID_REPORT: {
    tr: "Lütfen geçerli bir rapor nedeni seçin.",
    en: "Please select a valid report reason.",
  },
  RATE_LIMITED: {
    tr: "Çok fazla istek gönderdiniz. Biraz sonra yeniden deneyin.",
    en: "Too many requests. Please try again shortly.",
  },
  ORIGIN_DENIED: {
    tr: "Bu kaynaktan gelen isteğe izin verilmiyor.",
    en: "Requests from this origin are not allowed.",
  },
  REQUIRED_FIELDS: {
    tr: "Tüm zorunlu alanları tamamlayın.",
    en: "Complete all required fields.",
  },
};

const defaultErrorCopy: LocalizedCopy = {
  tr: "İşlem tamamlanamadı. Lütfen yeniden deneyin.",
  en: "The request could not be completed. Please try again.",
};

function interpolate(value: string, replacements?: Replacements) {
  if (!replacements) return value;
  return Object.entries(replacements).reduce(
    (result, [key, replacement]) =>
      result.replaceAll(`{{${key}}}`, String(replacement)),
    value,
  );
}

function fallbackLabel(value: string) {
  return value.replaceAll("_", " ").replaceAll("-", " ");
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "tr";
    } catch {
      return "tr";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {}
    document.documentElement.lang = language;
    document.title = "Dora AI";
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (description) {
      description.content =
        language === "tr"
          ? "Dora AI yaratıcı görsel ve video stüdyosu"
          : "Dora AI creative image and video studio";
    }
  }, [language]);

  const value = useMemo<LanguageValue>(() => {
    const translate = (copy: LocalizedCopy, replacements?: Replacements) =>
      interpolate(copy[language], replacements);
    const label = (source: Record<string, LocalizedCopy>, raw: string) =>
      source[raw] ? translate(source[raw]) : fallbackLabel(raw);

    return {
      language,
      setLanguage,
      t: translate,
      error: (caught, fallback = defaultErrorCopy) => {
        const code =
          typeof caught === "object" &&
          caught !== null &&
          "code" in caught &&
          typeof caught.code === "string"
            ? caught.code
            : "";
        if (code && errorCopy[code]) return translate(errorCopy[code]);
        if (
          language === "en" &&
          caught instanceof Error &&
          caught.message.trim()
        ) {
          return caught.message;
        }
        return translate(fallback);
      },
      status: (raw) => label(statusCopy, raw),
      kind: (raw) => label(kindCopy, raw),
      mode: (raw) => label(modeCopy, raw),
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) throw new Error("LanguageProvider missing");
  return value;
}
